import { TourAnchor } from "training/anchors";
import {
  abnApplies,
  offersPdf,
  showsBenefits,
} from "training/engine/conditions";
import { ITourStep } from "training/types";

import { ESTIMATE_ACTIONS_MENU, ESTIMATE_ACTIONS_MENU_FIRST } from "./shared";

/**
 * CLE-3015 Module 3: Finding, viewing and editing an estimate. Text is the curriculum's (curriculum-v3,
 * 2026-10-21), verbatim, except where a comment says otherwise.
 *
 * Reorder: 3.10 (the "..." menu on your row, Estimates tab) runs right after 3.1 (the search box), before
 * 3.2 opens the estimate — both are on the Estimates tab, and in the curriculum's order 3.10 would send the
 * trainee back to the hospital view and then on to Module 4 on the estimate again. Ids are unchanged.
 *
 * The Estimate Details page was redesigned (CLE-2961) after the curriculum was checked. What changed for
 * these steps, all verified against the code on 2026-10-21:
 * - The page's title is the patient's name with the status chip next to it; there is no "Estimate
 *   Details" heading. 3.2's text is kept.
 * - The Patient card's title is "Patient"; "Outreach Enabled" and its three buttons are at the card's foot.
 * - "Patient Benefits" is titled "Insurance & Benefits"; "Service Details" is the table inside "Provider
 *   Calculations".
 * - Duplicate and Download ABN are in the "..." menu; each such step has the menu `opener` (see
 *   ./shared). Download PDF is a header button since CLE-3018.
 *
 * Instruction-column words: where the curriculum's "Trainee clicks or looks at" column says what to do
 * ("Type your last name", "Click it, then Cancel"), the step text carries that instruction, because a
 * click or fill step has no other way to say it.
 *
 * Open-cancel rows (3.4, 3.5, 3.8) split at a sentence boundary: the click on the pencil / menu item says
 * what to click; the explanation shows on the Cancel / No step, while the edit mode or dialog is open.
 */
export const MODULE_3: ITourStep[] = [
  {
    id: "m3.01-search",
    module: 3,
    anchor: TourAnchor.SearchBox,
    label: "Search by patient name, payer, or account number",
    mode: "fill",
    route: "estimates",
    // "Type your last name." is the curriculum's instruction column.
    text: "Type your last name. This is how you find a patient who calls or walks in. Name, account number or member ID all work.",
  },
  {
    // Moved up from the end of Module 3 (see the header).
    id: "m3.10-row-menu",
    module: 3,
    anchor: TourAnchor.RowMenuFirstRow,
    label: "Estimate actions",
    mode: "look",
    route: "estimates",
    text: "Resend Estimate, Duplicate Estimate, Copy Estimate URL and Cancel Estimate are here too, so you do not have to open the estimate for the common ones.",
  },
  {
    id: "m3.02-view",
    module: 3,
    anchor: TourAnchor.ViewFirstRow,
    label: "View",
    mode: "click",
    // View opens /orders/:id itself. If the top row is not the trainee's own estimate, the Estimate
    // Details host sends them to theirs (TrainingTourHost).
    advanceOn: "route",
    route: "estimates",
    // Instruction column.
    text: 'Click "View" on your row.',
  },
  {
    id: "m3.02b-estimate-details",
    module: 3,
    anchor: TourAnchor.EstimateHeader,
    mode: "look",
    route: "estimate",
    // The page loads the estimate after the navigation.
    waitMs: 8000,
    text: "This is the Estimate Details page. The chip next to the title is the status. Everything about this visit lives here.",
  },
  {
    id: "m3.03-outreach",
    module: 3,
    anchor: TourAnchor.PatientOutreach,
    label: "Outreach Enabled",
    mode: "look",
    route: "estimate",
    text: "Outreach is the texts we send. Stop it if the patient asks us not to contact them. Resend sends the same text again, for example if they deleted it. Send Custom Text is a one-off message you write yourself. We come back to these in Module 5.",
  },
  {
    id: "m3.04-patient-pencil",
    module: 3,
    anchor: TourAnchor.PatientEdit,
    mode: "open-cancel",
    route: "estimate",
    // Instruction column ("The pencil on the Patient Details card. Click it"); the card is now "Patient".
    text: "Click the pencil on the Patient card.",
    followUp: {
      anchor: TourAnchor.PatientEditCancel,
      label: "Cancel",
      text: "Name, date of birth, phone and email can be fixed here. Careful: saving a new phone or email sends the estimate again to the new number. Click Cancel for now.",
    },
  },
  {
    id: "m3.05-visit-pencil",
    module: 3,
    anchor: TourAnchor.VisitEdit,
    mode: "open-cancel",
    route: "estimate",
    // Instruction column.
    text: 'Click the pencil on the "Visit" card.',
    followUp: {
      anchor: TourAnchor.VisitEditCancel,
      label: "Cancel",
      text: 'Date and time of service and the Account / Visit ID are fixed here, and so is "Not scheduled". Changing the date restarts the reminder texts from the new date. Cancel.',
    },
  },
  {
    id: "m3.06-benefits",
    module: 3,
    anchor: TourAnchor.PatientBenefits,
    label: "Insurance & Benefits",
    mode: "look",
    route: "estimate",
    when: showsBenefits,
    text: "What the payer told us about this patient's plan when we ran eligibility.",
  },
  {
    // 3.7 is two highlights (the Service Details table, then Redo Estimate), split at a sentence boundary.
    id: "m3.07-service-details",
    module: 3,
    anchor: TourAnchor.ServiceDetails,
    label: "Service Details",
    mode: "look",
    route: "estimate",
    text: "There is no pencil here on purpose.",
  },
  {
    id: "m3.07b-redo-estimate",
    module: 3,
    anchor: TourAnchor.RedoEstimate,
    label: "Redo",
    mode: "look",
    route: "estimate",
    text: "To change the insurance, the codes or the benefits, use Redo: it rebuilds the whole estimate and texts the patient the new one. Do not click it now.",
  },
  {
    id: "m3.08-duplicate",
    module: 3,
    anchor: TourAnchor.DuplicateEstimate,
    label: "Duplicate Estimate",
    mode: "open-cancel",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU_FIRST,
    // Instruction column.
    text: 'Click "Duplicate Estimate" and read the dialog.',
    followUp: {
      anchor: TourAnchor.DuplicateNo,
      label: "No",
      // The curriculum's text, then its instruction ("click No").
      text: 'For a patient coming back for a second visit, or for a bundle you build often. It copies everything, including the patient, so change every patient field before you send. Then run eligibility again. There is no separate template feature. Click "No".',
    },
  },
  {
    // Not curriculum text — the 3.9 branch "Download ABN appears when an ABN applies". Kept before the PDF so the
    // step ids and the walk's order stay stable; since CLE-3018 the PDF is a header button, so whether the menu is
    // still open when it is highlighted no longer matters.
    id: "m3.09a-download-abn",
    module: 3,
    anchor: TourAnchor.DownloadAbn,
    label: "Download ABN",
    mode: "look",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU,
    when: abnApplies,
    optional: true,
    waitMs: 2000,
    text: 'An ABN (Advance Beneficiary Notice) applies to a code on this estimate, so "Download ABN" is here too: the one the patient signed at checkout, or a blank one to print and sign.',
  },
  {
    id: "m3.09-download-pdf",
    module: 3,
    anchor: TourAnchor.DownloadPdf,
    label: "Download PDF",
    mode: "click",
    route: "estimate",
    when: offersPdf,
    text: 'This is the Good Faith Estimate for the patient\'s file or chart. You can print it, mail it or upload it. Go ahead and open it. If a box asks for a note for the PDF, type "training" and continue.',
  },
  {
    // The "Add Note for PDF" box, on hospitals whose PDF settings ask for a note first.
    id: "m3.09b-pdf-note",
    module: 3,
    anchor: TourAnchor.PdfNote,
    mode: "fill",
    route: "estimate",
    when: offersPdf,
    optional: true,
    waitMs: 2500,
    text: 'If a box asks for a note for the PDF, type "training" and continue.',
  },
  {
    // Not curriculum text.
    id: "m3.09c-pdf-note-save",
    module: 3,
    anchor: TourAnchor.PdfNoteSave,
    label: "Save",
    mode: "click",
    route: "estimate",
    when: offersPdf,
    optional: true,
    waitMs: 1000,
    text: 'Click "Save" to download it.',
  },
  {
    id: "m3.11-expired-card",
    module: 3,
    mode: "card",
    route: "estimate",
    text: 'An estimate whose date of service passed without payment becomes Expired a set number of days later. On the Estimate Details page its status chip offers "Change status to Sent" if the visit was rescheduled.',
  },
  {
    id: "m3.12-action-needed-card",
    module: 3,
    mode: "card",
    route: "estimate",
    text: 'Action Needed estimates open with a "Review" button instead of View. The review page looks like the create page with "Create and Send" at the bottom.',
  },
];
